import React, { useRef } from "react";
import { Controller } from "react-hook-form";
import PropTypes from "prop-types";
import { get as _get, isEqual } from "lodash";
import {
  Box,
  FormHelperText,
  Link,
  Skeleton,
  TextField,
} from "@mui/material";

import Autocomplete from "@mui/material/Autocomplete";
import useStyles from "./style";

function formatLoadError(error) {
  if (error == null) {
    return null;
  }
  if (typeof error === "string") {
    return error;
  }
  if (error.message) {
    return error.message;
  }
  try {
    return JSON.stringify(error);
  } catch (_err) {
    return "Failed to load options";
  }
}

function FormSelect({
  defaultValue,
  description,
  name,
  label,
  options,
  control,
  groupBy,
  errors,
  isDisabled,
  isMulti,
  isAutoComplete,
  onChange: onChangeProp,
  isLoading,
  loadError,
  onRetry,
  size,
  background,
  getOptionLabel,
  disableClearable,
  getOptionDisabled,
  startAdornment,
  freeSolo
}) {
  const classes = useStyles();
  const inputRef = useRef(null);
  const isError = _get(errors, name) !== undefined;
  const descriptionId = `${name}_description`;
  const loadErrorMessage = formatLoadError(loadError);

  // While loading, the whole field is replaced by plain skeleton lines. No input
  // chrome (label, underline, dropdown arrow) is drawn, so it reads as content
  // arriving rather than as a greyed-out control.
  if (isLoading) {
    return (
      <Box mt={1} mb={1}>
        <Skeleton
          variant="text"
          animation="wave"
          width="30%"
          className={classes.loadingLabelLine}
        />
        <Skeleton
          variant="text"
          animation="wave"
          width="70%"
          className={classes.loadingValueLine}
        />
        {description ? (
          <Skeleton
            variant="text"
            animation="wave"
            width="50%"
            className={classes.loadingDescriptionLine}
          />
        ) : null}
      </Box>
    );
  }

  return (
    <Box mt={1} mb={1}>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue || null}
        onFocus={() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }}
        render={({ field }) => (
            <Autocomplete
              disabled={isDisabled || Boolean(loadErrorMessage)}
              disableClearable={disableClearable}
              className={
                background !== "none" && classes[background]
                  ? classes[background]
                  : ""
              }
              size={size}
              value={field.value}
              options={options}
              groupBy={groupBy}
              filterSelectedOptions
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={(option, value) => isEqual(option, value)}
              freeSolo={freeSolo}
              renderInput={(params) => {
                let inputProps = {
                  ...params.InputProps
                };
                if (startAdornment) {
                  inputProps["startAdornment"] = startAdornment;
                }

                return (
                  <TextField
                    {...params}
                    error={isError || Boolean(loadErrorMessage)}
                    InputProps={inputProps}
                    name={name}
                    inputRef={inputRef}
                    label={label}
                    disabled={isDisabled || Boolean(loadErrorMessage)}
                    variant="standard"
                  />
                );
              }}
              onChange={(_, data) => {
                field.onChange(data);
                if (onChangeProp) {
                  if (isMulti) {
                    if (Array.isArray(data)) {
                      onChangeProp(
                        name,
                        data.map((option) => option.value)
                      );
                    } else {
                      // form clear option
                      onChangeProp(name, []);
                    }
                  } else {
                    // sigle option dropdown
                    if (data && !Array.isArray(data)) {
                      onChangeProp(name, data.value);
                    } else {
                      // form clear option
                      onChangeProp(name, "");
                    }
                  }
                }
              }}
              multiple={isMulti}
              getOptionDisabled={getOptionDisabled}
            />
        )}
      />
      <FormHelperText id={descriptionId}>{description}</FormHelperText>
      {loadErrorMessage && (
        <FormHelperText className={classes.error} component="div">
          {loadErrorMessage}
          {onRetry ? (
            <>
              {" "}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onRetry}
                className={classes.retryLink}
              >
                Retry
              </Link>
            </>
          ) : null}
        </FormHelperText>
      )}
      {isError && (
        <FormHelperText className={classes.error}>
          {_get(errors, name).message}
        </FormHelperText>
      )}
    </Box>
  );
}

FormSelect.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.array,
  errors: PropTypes.object,
  control: PropTypes.any,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array
  ]),
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isAutoComplete: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  onRetry: PropTypes.func,
  onChange: PropTypes.func,
  size: PropTypes.string,
  background: PropTypes.string,
  disableClearable: PropTypes.bool,
  groupBy: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionDisabled: PropTypes.func,
  startAdornment: PropTypes.element,
  freeSolo: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array
  ])
};

FormSelect.defaultProps = {
  description: "",
  freeSolo: false,
  isLoading: false,
  loadError: null,
  onRetry: undefined,
  isMulti: false,
  isDisabled: false,
  isAutoComplete: true,
  size: "medium",
  background: "none",
  onChange: () => {},
  disableClearable: false,
  groupBy: () => {},
  getOptionLabel: (option) =>
    option && option.label ? option.label.toString() : "",
  getOptionDisabled: () => {}
};

export default FormSelect;
